import Link from "@/components/Link";
import { getBlogCategoryUrl, getBlogAuthorUrl } from "@/lib/blog";
import { cn, sanitizeHTML } from "@/lib/utils";
import Image from "next/image";
import { getReadableImageUrl } from "@/lib/directus";
import { htmlToPlainText, cleanBlogContent } from "../utils";

import Button, { ButtonType } from "@/components/reusable/Button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import ResponsiveImage from "@/components/ui/ResponsiveImage";

export interface BlogPostSectionData {
  title: string;
  published_at: string;
  content: string;
  featured_image?: {
    id: string;
    filename_download: string;
  };
  hero_image: any[];
  author?: {
    slug: string;
    title: string;
  };
  categories: {
    blog_categories_id: {
      title: string;
      slug: string;
    };
  }[];
  previousPost?: {
    slug: string;
  };
  nextPost?: {
    slug: string;
  };
}

interface BlogPostSectionProps {
  data: BlogPostSectionData;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch (e) {
    return dateString;
  }
};

export default function BlogPostSection({ data }: BlogPostSectionProps) {
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = htmlToPlainText(data.title) || "";

  console.log(data.author);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      shareUrl
    )}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      shareUrl
    )}`,
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    }
  };

  return (
    <>
      <section className="BlogHeroPostSection w-full bg-accent-color-three">
        <div className="relative aspect-[16/9] md:aspect-[16/9] desktop:aspect-[16/10] w-screen md:w-full h-[80vh]">
          <ResponsiveImage
            desktop_image={
              data.hero_image?.length
                ? data.hero_image[0].item.desktop_image
                : "/blog-hero-desktop.webp"
            }
            mobile_image={
              data.hero_image?.length
                ? data.hero_image[0].item.mobile_image
                : "/blog-hero-mobile.webp"
            }
            tablet_image={
              data.hero_image?.length
                ? data.hero_image[0].item.tablet_image
                : "/blog-hero-tablet.webp"
            }
            alt={
              data.hero_image?.length
                ? data.hero_image[0].item.imageAlt
                : data.title
            }
            className="object-cover bg-accent-color-three"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            quality={95}
            loading="lazy"
            shouldFetch={data.hero_image.length > 0}
          />
        </div>
      </section>
      <section className="bg-accent-color-three-light md:px-12 md:pb-16 desktop:pb-36">
        <div
          className={
            "desktop:max-w-[960px] isolate mx-auto md:mt-[-20%] bg-accent-color-three py-6 px-6 md:py-16 md:px-6 desktop:px-20 desktop:py-16 gap-12 desktop:gap-16 flex flex-col"
          }
        >
          <div className="flex flex-col gap-3">
            <ul className="flex flex-wrap divide-x-2 divide-secondary justify-center">
              {data.categories?.map((category, index) => (
                <Button
                  key={category.blog_categories_id.slug}
                  type={ButtonType.primaryGhost}
                  link={getBlogCategoryUrl(category.blog_categories_id.slug)}
                  className="hover:underline py-0 h-fit text-medium-special-heading"
                >
                  {category.blog_categories_id.title}
                </Button>
              ))}
            </ul>

            <h1 className="text-blog-h1-mobile md:text-blog-h1-tablet desktop:text-blog-h1-desktop font-serif text-center text-accent-color-two">
              {data.title}
            </h1>

            <span className="text-white text-paragraph-large text-center">
              {formatDate(data.published_at)}
            </span>

            <span className="font-sans text-accent-color-two text-paragraph-small text-center">
              Written by{" "}
              <Link href="/about/dr-matthias-solomon/">Dr. Solomon</Link>
            </span>
          </div>
          {data.featured_image && (
            <Image
              src={getReadableImageUrl(
                data.featured_image.id,
                data.featured_image.filename_download
              )}
              alt={htmlToPlainText(data.title) || ""}
              width={800}
              height={400}
              className="object-cover bg-accent-color-three self-center"
              sizes="w-full"
              quality={95}
              loading="lazy"
            />
          )}
          <div
            className="text-[#fff] grid gap-8 font-sans text-paragraph-large
            [&_div]:!w-full [&_div_*_div]:!w-full            
            [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:mb-6
            [&_img]:w-full [&_img]:h-auto [&_img]:my-6
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:grid [&_ul]:gap-2
            [&_li]:pl-1
            [&_h2]:text-h2-tablet [&_h2]:desktop:text-h2-desktop [&_h2]:text-accent-color-two [&_h2]:font-serif [&_h2]:py-2
            [&_h3]:text-h3-tablet [&_h3]:desktop:text-h3-desktop [&_h3]:text-accent-color-two [&_h3]:font-serif [&_h3]:py-2"
            dangerouslySetInnerHTML={sanitizeHTML(
              cleanBlogContent(data.content)
            )}
          ></div>

          <footer className="desktop:mt-20 flex items-center justify-between gap-3 flex-col md:flex-row">
            <Button
              type={ButtonType.tertiary}
              link={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${data.previousPost?.slug}`}
              aria-hidden={!data.previousPost}
              disabled={!data.previousPost}
              className={cn(
                "w-full md:w-auto",
                !data.previousPost &&
                  "bg-transparent text-transparent select-none border-transparent"
              )}
              leftIcon={<ArrowLeft className="w-6 h-6" />}
            >
              Previous Post
            </Button>

            <div className="flex justify-center items-center gap-4">
              <Button
                type={ButtonType.tertiaryOutline}
                link={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-[14px] border-2"
                aria-label="Share on Facebook"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                </svg>
              </Button>
              <Button
                type={ButtonType.tertiaryOutline}
                link={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-[14px] border-2"
                aria-label="Share on Twitter"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </Button>
              <Button
                type={ButtonType.tertiaryOutline}
                link={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-[14px] border-2"
                aria-label="Share on LinkedIn"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </Button>
            </div>

            <Button
              type={ButtonType.tertiary}
              link={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${data.nextPost?.slug}`}
              aria-hidden={!data.nextPost}
              disabled={!data.nextPost}
              className={cn(
                "w-full md:w-auto",
                !data.nextPost &&
                  "bg-transparent text-transparent select-none border-transparent"
              )}
              rightIcon={<ArrowRight className="w-6 h-6" />}
            >
              Next Post
            </Button>
          </footer>
        </div>
      </section>
    </>
  );
}
